import { AppShell } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Outlet, useLocation } from 'react-router-dom';
import { FABDataEntry } from '@/components/FABDataEntry';
import { NotificationErrorBanner } from '@/components/NotificationErrorBanner';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { OnboardingGate } from '@/components/OnboardingGate';
import { UploadProgressStrip } from '@/features/livestock/UploadProgressStrip';
import { useRefreshLivestockOnInboxExit } from '@/hooks/usePhotoInbox';
import { AppHeader } from './AppHeader';
import { NavbarSimple } from './NavbarSimple';

// Pages where FAB should be hidden: ICP test details, and the photo inbox,
// whose assign view has a bottom action bar
const FAB_HIDDEN_ROUTES = ['/icp-tests/', '/livestock/photos'];

export function ProtectedLayout(): JSX.Element {
  const [mobileOpened, { toggle: toggleMobile, close: closeMobile }] = useDisclosure();
  const [desktopOpened, { toggle: toggleDesktop }] = useDisclosure(true);
  const location = useLocation();
  // Thumbnails of organisms that got photos are refreshed once the user leaves the inbox.
  useRefreshLivestockOnInboxExit(location.pathname);

  // Hide FAB on ICP detail pages (but not the main /icp-tests list)
  const showFAB = !FAB_HIDDEN_ROUTES.some(route =>
    location.pathname.startsWith(route) && location.pathname !== '/icp-tests'
  );

  return (
    <ProtectedRoute>
      <OnboardingGate />
      <AppShell
        header={{ height: 60 }}
        navbar={{
          width: 250,
          breakpoint: 'md',
          collapsed: { mobile: !mobileOpened, desktop: !desktopOpened },
        }}
        padding="md"
      >
        <AppShell.Header>
          <AppHeader mobileOpened={mobileOpened} toggleMobile={toggleMobile} toggleDesktop={toggleDesktop} desktopOpened={desktopOpened} />
        </AppShell.Header>
        <AppShell.Navbar>
          <NavbarSimple onNavigate={closeMobile} />
        </AppShell.Navbar>
        <AppShell.Main>
          <NotificationErrorBanner />
          <Outlet />
          {/* Photo uploads keep going across pages; after the Outlet so its spacer ends the page. */}
          <UploadProgressStrip fabVisible={showFAB} />
        </AppShell.Main>
      </AppShell>
      {showFAB && <FABDataEntry />}
    </ProtectedRoute>
  );
}
